import React, { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { isWissenEmail } from '../utils/validate';
import { PodDetail, Avatar } from '../components/DetailViews';

const COLUMNS = [
  { key: 'todo', label: 'TO DO', color: 'var(--txt2)' },
  { key: 'in_progress', label: 'IN PROGRESS', color: 'var(--j1)' },
  { key: 'review', label: 'REVIEW', color: 'var(--j4)' },
  { key: 'done', label: 'DONE', color: 'var(--j5)' },
];
const PRIORITY_COLOR = { low: 'var(--txt2)', medium: 'var(--j4)', high: 'var(--j6)' };
const STATUS_LABEL = { todo: 'To Do', in_progress: 'In Progress', review: 'Review', done: 'Done' };

function ProgressBar({ value, color = 'var(--j1)' }) {
  return (
    <div style={{ background: 'var(--bg4)', borderRadius: 3, height: 6, overflow: 'hidden' }}>
      <div style={{ width: `${value}%`, height: 6, borderRadius: 3, background: `linear-gradient(90deg, var(--j2), ${color})`, transition: 'width 0.5s ease' }} />
    </div>
  );
}

export default function Interns() {
  const { user, can } = useAuth();
  const canManage = can('cwg', 'stakeholder', 'sme');
  const canAdmin = can('cwg', 'stakeholder');

  const [interns, setInterns] = useState([]);
  const [pods, setPods] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showInternForm, setShowInternForm] = useState(false);
  const [showPodForm, setShowPodForm] = useState(false);
  const [detail, setDetail] = useState(null); // task being viewed/edited
  const [openPod, setOpenPod] = useState(null);

  const blankTask = { title: '', description: '', podId: '', assigneeId: '', priority: 'medium', dueDate: '' };
  const [taskForm, setTaskForm] = useState(blankTask);
  const [internForm, setInternForm] = useState({ name: '', email: '', password: '' });
  const [podForm, setPodForm] = useState({ name: '', ownerId: '', internIds: [] });

  const loadPods = () => api.get('/intern-pods').then(r => setPods(r.data)).catch(() => {});
  const loadTasks = () => api.get('/tasks').then(r => setTasks(r.data)).catch(() => {});
  const loadInterns = () => { if (canManage) api.get('/interns').then(r => setInterns(r.data)).catch(() => {}); };

  useEffect(() => { loadPods(); loadTasks(); loadInterns(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => ({
    interns: interns.length,
    pods: pods.length,
    active: tasks.filter(t => t.status !== 'done').length,
    done: tasks.filter(t => t.status === 'done').length,
  }), [interns, pods, tasks]);

  // ── actions ──
  const autoCreatePods = async () => {
    try {
      const { data } = await api.post('/intern-pods/auto', { size: 4 });
      await loadPods();
      showToast(`Auto-created ${data.count} pod${data.count === 1 ? '' : 's'}`);
    } catch (e) { showToast(e.response?.data?.error || 'Failed to auto-create pods', 'error'); }
  };

  const createPod = async () => {
    if (!podForm.name.trim()) return showToast('Pod name is required', 'error');
    try {
      const { data } = await api.post('/intern-pods', podForm);
      setPods(p => [...p, data]); setShowPodForm(false); setPodForm({ name: '', ownerId: '', internIds: [] });
      showToast('Pod created');
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };

  const addIntern = async () => {
    if (!internForm.name.trim() || !internForm.password.trim()) return showToast('All fields required', 'error');
    if (!isWissenEmail(internForm.email)) return showToast('Email must be a @wissen.com address', 'error');
    if (internForm.password.length < 6) return showToast('Password must be at least 6 characters', 'error');
    try {
      const { data } = await api.post('/interns', internForm);
      setInterns(p => [...p, data]); setShowInternForm(false); setInternForm({ name: '', email: '', password: '' });
      showToast(`${data.name} added to the intern program`);
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };

  const createTask = async () => {
    if (!taskForm.title.trim()) return showToast('Task title is required', 'error');
    try {
      const { data } = await api.post('/tasks', taskForm);
      setTasks(p => [data, ...p]); setShowTaskForm(false); setTaskForm(blankTask);
      await loadPods();
      showToast('Task allocated');
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };

  const patchTask = async (id, body) => {
    try {
      const { data } = await api.patch(`/tasks/${id}`, body);
      setTasks(p => p.map(t => t.id === id ? data : t));
      setDetail(d => (d && d.id === id ? data : d));
      await loadPods();
      return data;
    } catch (e) { showToast(e.response?.data?.error || 'Failed to update', 'error'); }
  };

  // interns eligible for the selected pod (or all if no pod)
  const assignableInterns = useMemo(() => {
    const pod = pods.find(p => p.id === taskForm.podId);
    if (pod) return pod.interns || [];
    return interns;
  }, [taskForm.podId, pods, interns]);

  const isOwnerOf = (task) => {
    const pod = pods.find(p => p.id === task?.podId);
    return canAdmin || task?.createdById === user?.id || (pod && pod.ownerId === user?.id);
  };

  return (
    <div>
      <style>{`
        @keyframes taskIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        @keyframes podIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        .task-card { animation: taskIn 0.35s ease both; transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s; cursor: pointer; }
        .task-card:hover { transform: translateY(-3px); box-shadow: 0 6px 20px rgba(0,212,255,0.12); border-color: var(--brd) !important; }
        .pod-card { animation: podIn 0.4s ease both; }
      `}</style>

      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}

      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div><div className="page-title">🎓 INTERN PROGRAM</div><div className="page-sub">Task allocation · Pods · Live progress tracking</div></div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canManage && <button className="btn btn-primary" onClick={() => { setTaskForm(blankTask); setShowTaskForm(true); }}>+ NEW TASK</button>}
          {canManage && <button className="btn" style={{ borderColor: 'var(--brd)', color: 'var(--j1)' }} onClick={() => setShowPodForm(true)}>+ NEW POD</button>}
          {canAdmin && <button className="btn" style={{ borderColor: 'var(--brd)', color: 'var(--j1)' }} onClick={autoCreatePods}>⚙ AUTO-CREATE PODS</button>}
          {canAdmin && <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setShowInternForm(true)}>+ ADD INTERN</button>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid-4" style={{ marginBottom: 20 }}>
        {[{ v: stats.interns, l: 'Interns' }, { v: stats.pods, l: 'Intern Pods' }, { v: stats.active, l: 'Active Tasks' }, { v: stats.done, l: 'Completed' }].map(s => (
          <div key={s.l} className="card"><div className="stat-val">{s.v}</div><div className="stat-label">{s.l}</div></div>
        ))}
      </div>

      {/* Pods */}
      <div className="section-header"><div className="section-title">INTERN PODS</div></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, marginBottom: 28 }}>
        {pods.map((pod, i) => (
          <div key={pod.id} className="card pod-card" style={{ animationDelay: `${i * 60}ms`, borderTop: `2px solid ${pod.color}`, cursor: 'pointer' }} onClick={() => setOpenPod(pod)} title="View pod details & charts">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 700, color: 'var(--txt)' }}>{pod.name}</div>
              {pod.autoCreated && <span style={{ fontSize: 8, color: pod.color, fontFamily: 'Share Tech Mono', border: `1px solid ${pod.color}55`, borderRadius: 2, padding: '1px 5px' }}>AUTO</span>}
            </div>
            <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', marginBottom: 10 }}>Owner: {pod.ownerName}</div>
            <div style={{ display: 'flex', marginBottom: 10 }}>
              {(pod.interns || []).map((m, j) => (
                <div key={m.id} title={m.name} style={{ marginLeft: j === 0 ? 0 : -6 }}><Avatar user={m} size={26} /></div>
              ))}
              {(pod.interns || []).length === 0 && <span style={{ fontSize: 11, color: 'var(--txt3)' }}>No interns</span>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', marginBottom: 5 }}>
              <span>{pod.doneCount}/{pod.taskCount} tasks done</span><span style={{ color: pod.color }}>{pod.progress}%</span>
            </div>
            <ProgressBar value={pod.progress} color={pod.color} />
          </div>
        ))}
        {pods.length === 0 && <div className="empty-state" style={{ gridColumn: '1/-1' }}>No pods yet. {canAdmin ? 'Use “Auto-create pods” to form them from interns.' : ''}</div>}
      </div>

      {/* Task board */}
      <div className="section-header"><div className="section-title">TASK BOARD</div><span style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>{tasks.length} tasks</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, alignItems: 'start' }}>
        {COLUMNS.map(col => {
          const colTasks = tasks.filter(t => t.status === col.key);
          return (
            <div key={col.key} style={{ background: 'rgba(2,4,8,0.5)', border: '1px solid var(--brd2)', borderRadius: 6, padding: 10, minHeight: 120 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontFamily: 'Share Tech Mono', fontSize: 11, letterSpacing: 1, color: col.color }}>{col.label}</span>
                <span style={{ fontFamily: 'Orbitron', fontSize: 12, color: 'var(--txt3)' }}>{colTasks.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {colTasks.map((t, i) => (
                  <div key={t.id} className="card task-card" style={{ padding: 12, animationDelay: `${i * 50}ms` }} onClick={() => setDetail(t)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, marginBottom: 6 }}>
                      <div style={{ fontWeight: 600, color: 'var(--txt)', fontSize: 13, lineHeight: 1.3 }}>{t.title}</div>
                      <span style={{ fontSize: 8, fontFamily: 'Share Tech Mono', color: PRIORITY_COLOR[t.priority], border: `1px solid ${PRIORITY_COLOR[t.priority]}55`, borderRadius: 2, padding: '1px 4px', height: 'fit-content', textTransform: 'uppercase', flexShrink: 0 }}>{t.priority}</span>
                    </div>
                    {t.podName && <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', marginBottom: 6 }}>{t.podName}</div>}
                    <div style={{ marginBottom: 8 }}><ProgressBar value={t.progress} /></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(0,212,255,0.12)', border: '1px solid var(--brd)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: 'var(--j1)' }}>{t.assigneeName?.split(' ').map(w => w[0]).slice(0, 2).join('') || '–'}</div>
                        <span style={{ fontSize: 9, color: 'var(--txt2)' }}>{t.assigneeName}</span>
                      </div>
                      {t.dueDate && <span style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>{new Date(t.dueDate).toLocaleDateString()}</span>}
                    </div>
                  </div>
                ))}
                {colTasks.length === 0 && <div style={{ fontSize: 10, color: 'var(--txt3)', textAlign: 'center', padding: '14px 0', fontFamily: 'Share Tech Mono' }}>—</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* New Task modal */}
      {showTaskForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <button className="modal-close" onClick={() => setShowTaskForm(false)}>×</button>
            <div className="modal-title">ALLOCATE TASK</div>
            <div className="form-group"><label className="label">Title</label><input className="input" value={taskForm.title} onChange={e => setTaskForm(p => ({ ...p, title: e.target.value }))} placeholder="What needs to be done?" /></div>
            <div className="form-group"><label className="label">Description</label><textarea className="input" rows={3} value={taskForm.description} onChange={e => setTaskForm(p => ({ ...p, description: e.target.value }))} placeholder="Details, acceptance criteria…" /></div>
            <div className="grid-2">
              <div className="form-group"><label className="label">Pod</label>
                <select className="input" value={taskForm.podId} onChange={e => setTaskForm(p => ({ ...p, podId: e.target.value, assigneeId: '' }))}>
                  <option value="">— No pod —</option>
                  {pods.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="label">Assignee (intern)</label>
                <select className="input" value={taskForm.assigneeId} onChange={e => setTaskForm(p => ({ ...p, assigneeId: e.target.value }))}>
                  <option value="">— Unassigned —</option>
                  {assignableInterns.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group"><label className="label">Priority</label>
                <select className="input" value={taskForm.priority} onChange={e => setTaskForm(p => ({ ...p, priority: e.target.value }))}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
              </div>
              <div className="form-group"><label className="label">Due date</label><input type="date" className="input" value={taskForm.dueDate} onChange={e => setTaskForm(p => ({ ...p, dueDate: e.target.value }))} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setShowTaskForm(false)}>CANCEL</button>
              <button className="btn btn-primary" onClick={createTask} disabled={!taskForm.title.trim()}>ALLOCATE</button>
            </div>
          </div>
        </div>
      )}

      {/* New Pod modal */}
      {showPodForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 480 }}>
            <button className="modal-close" onClick={() => setShowPodForm(false)}>×</button>
            <div className="modal-title">NEW INTERN POD</div>
            <div className="form-group"><label className="label">Pod name</label><input className="input" value={podForm.name} onChange={e => setPodForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Intern Pod — GenAI" /></div>
            <div className="form-group"><label className="label">Interns</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {interns.map(m => {
                  const on = podForm.internIds.includes(m.id);
                  return <button key={m.id} className="btn btn-sm" style={{ borderColor: on ? 'var(--j1)' : 'var(--brd2)', color: on ? 'var(--j1)' : 'var(--txt2)', background: on ? 'rgba(0,212,255,0.08)' : 'transparent' }}
                    onClick={() => setPodForm(p => ({ ...p, internIds: on ? p.internIds.filter(x => x !== m.id) : [...p.internIds, m.id] }))}>{m.name}</button>;
                })}
                {interns.length === 0 && <span style={{ fontSize: 11, color: 'var(--txt3)' }}>No interns yet — add some first.</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setShowPodForm(false)}>CANCEL</button>
              <button className="btn btn-primary" onClick={createPod}>CREATE POD</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Intern modal */}
      {showInternForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <button className="modal-close" onClick={() => setShowInternForm(false)}>×</button>
            <div className="modal-title">ADD INTERN</div>
            <div className="form-group"><label className="label">Full name</label><input className="input" value={internForm.name} onChange={e => setInternForm(p => ({ ...p, name: e.target.value }))} placeholder="Intern name" /></div>
            <div className="form-group"><label className="label">Email (@wissen.com)</label><input className="input" type="email" value={internForm.email} onChange={e => setInternForm(p => ({ ...p, email: e.target.value }))} placeholder="name@wissen.com" /></div>
            <div className="form-group"><label className="label">Temporary password</label><input className="input" value={internForm.password} onChange={e => setInternForm(p => ({ ...p, password: e.target.value }))} placeholder="min 6 characters" /></div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setShowInternForm(false)}>CANCEL</button>
              <button className="btn btn-primary" onClick={addIntern}>ADD INTERN</button>
            </div>
          </div>
        </div>
      )}

      {/* Task detail modal */}
      {detail && (
        <TaskDetail task={detail} owner={isOwnerOf(detail)} interns={interns} onClose={() => setDetail(null)} onPatch={patchTask} />
      )}

      {/* Pod detail with charts */}
      {openPod && <PodDetail pod={openPod} variant="intern-pod" tasks={tasks} onClose={() => setOpenPod(null)} />}
    </div>
  );
}

// ── Task detail / update drawer ──────────────────────────────────────────────
function TaskDetail({ task, owner, interns, onClose, onPatch }) {
  const [progress, setProgress] = useState(task.progress);
  const [status, setStatus] = useState(task.status);
  const [comment, setComment] = useState('');
  const [priority, setPriority] = useState(task.priority);
  const [assigneeId, setAssigneeId] = useState(task.assigneeId || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const body = { status, progress, comment };
    if (owner) { body.priority = priority; body.assigneeId = assigneeId || null; }
    await onPatch(task.id, body);
    setSaving(false);
    setComment('');
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <button className="modal-close" onClick={onClose}>×</button>
        <div className="modal-title">{task.title}</div>
        <div style={{ fontSize: 11, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', marginBottom: 12 }}>
          {task.podName || 'No pod'} · {task.assigneeName} · created by {task.createdByName}
        </div>
        {task.description && <div style={{ fontSize: 13, color: 'var(--txt2)', lineHeight: 1.6, marginBottom: 16, background: 'rgba(0,212,255,0.03)', border: '1px solid var(--brd2)', borderRadius: 4, padding: 12 }}>{task.description}</div>}

        <div className="grid-2">
          <div className="form-group"><label className="label">Status</label>
            <select className="input" value={status} onChange={e => setStatus(e.target.value)}>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          {owner && (
            <div className="form-group"><label className="label">Priority</label>
              <select className="input" value={priority} onChange={e => setPriority(e.target.value)}>
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
          )}
        </div>

        {owner && (
          <div className="form-group"><label className="label">Assignee</label>
            <select className="input" value={assigneeId} onChange={e => setAssigneeId(e.target.value)}>
              <option value="">— Unassigned —</option>
              {interns.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        )}

        <div className="form-group">
          <label className="label">Progress: <span style={{ color: 'var(--j1)', fontFamily: 'Orbitron' }}>{progress}%</span></label>
          <input type="range" min="0" max="100" step="5" value={progress} onChange={e => setProgress(+e.target.value)} style={{ width: '100%', accentColor: 'var(--j1)' }} />
          <div style={{ marginTop: 6 }}><ProgressBar value={progress} /></div>
        </div>

        <div className="form-group"><label className="label">Add an update / comment</label>
          <input className="input" value={comment} onChange={e => setComment(e.target.value)} placeholder="What changed?" /></div>

        {/* Activity log */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1, marginBottom: 8 }}>ACTIVITY</div>
          <div style={{ maxHeight: 140, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[...(task.activity || [])].reverse().map((a, i) => (
              <div key={i} style={{ fontSize: 11, color: 'var(--txt2)', borderLeft: '2px solid var(--brd)', paddingLeft: 8 }}>
                <span style={{ color: 'var(--txt)' }}>{a.by}</span> · {a.text}
                <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>{new Date(a.at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={onClose}>CLOSE</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'SAVING…' : 'SAVE UPDATE'}</button>
        </div>
      </div>
    </div>
  );
}
